// src/cockpit4/components/RiskManager.tsx
// ============================================
// P1-功能5：风险管理（项目中心）
//
// 功能：
//   1. 风险自动识别（进度风险：延期任务；资源风险：过载成员；技术风险：阻塞任务；外部风险：依赖外部）
//   2. 风险概率和影响评估（高/中/低，可手动调整）
//   3. 风险应对建议（基于风险类型生成应对策略）
//   4. 风险跟踪和闭环（风险状态：开放/处理中/已闭环）
//   5. 风险矩阵可视化（概率 × 影响 的 3x3 矩阵）
//
// 数据来源：
//   - localStorage 读取任务列表（xeaos_tasks_list）自动识别风险
//   - localStorage 持久化风险记录（xeaos_risks）
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldAlert,
  Plus,
  X,
  RefreshCw,
  Loader2,
  Trash2,
  Pencil,
  AlertTriangle,
  Clock,
  Users,
  AlertOctagon,
  Globe,
  CheckCircle2,
  Sparkles,
  Lightbulb,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 风险类型 */
export type RiskType =
  | "schedule" // 进度风险
  | "resource" // 资源风险
  | "technical" // 技术风险
  | "external"; // 外部风险

/** 风险概率 */
export type RiskLevel = "high" | "medium" | "low";

/** 风险状态 */
export type RiskStatus = "open" | "handling" | "closed";

/** 风险模型 */
export interface Risk {
  id: string;
  title: string;
  description: string;
  type: RiskType;
  probability: RiskLevel;
  impact: RiskLevel;
  status: RiskStatus;
  /** 应对建议 */
  suggestion: string;
  /** 关联任务 ID（自动识别时） */
  relatedTaskId?: string;
  /** 来源：自动识别或手动添加 */
  source: "auto" | "manual";
  createdAt: string;
  updatedAt: string;
}

/** 复用 TaskBoard 的任务结构（只读取需要的字段） */
interface TaskLite {
  id: string;
  title: string;
  assignee: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "todo" | "in_progress" | "done" | "blocked";
  dueDate: string;
  createdAt: string;
  actualProgress: number;
}

// ============================================
// 常量配置
// ============================================

const RISKS_KEY = "xeaos_risks";
const TASKS_KEY = "xeaos_tasks_list";

const RISK_TYPE_META: Record<
  RiskType,
  { label: string; color: string; icon: typeof Clock }
> = {
  schedule: { label: "进度风险", color: "#F59E0B", icon: Clock },
  resource: { label: "资源风险", color: "#F97316", icon: Users },
  technical: { label: "技术风险", color: "#EF4444", icon: AlertOctagon },
  external: { label: "外部风险", color: "#8B5CF6", icon: Globe },
};

const LEVEL_META: Record<
  RiskLevel,
  { label: string; color: string }
> = {
  high: { label: "高", color: "#EF4444" },
  medium: { label: "中", color: "#F59E0B" },
  low: { label: "低", color: "#10B981" },
};

const STATUS_META: Record<
  RiskStatus,
  { label: string; color: string; icon: typeof AlertTriangle }
> = {
  open: { label: "开放", color: "#EF4444", icon: AlertTriangle },
  handling: { label: "处理中", color: "#F59E0B", icon: Clock },
  closed: { label: "已闭环", color: "#10B981", icon: CheckCircle2 },
};

/** 按类型生成应对建议 */
const RISK_SUGGESTIONS: Record<RiskType, string> = {
  schedule:
    "建议：1) 调整截止日期或拆分任务；2) 增加资源投入或调整优先级；3) 与干系人沟通范围裁剪。",
  resource:
    "建议：1) 重新分配任务，平衡负载；2) 引入外援或临时人手；3) 调整任务优先级，先做关键任务。",
  technical:
    "建议：1) 组织技术评审会议；2) 寻求外部专家支持；3) 准备备选技术方案；4) 增加技术验证迭代。",
  external:
    "建议：1) 建立外部依赖监控机制；2) 提前与外部干系人沟通；3) 准备应急预案和兜底方案。",
};

// ============================================
// localStorage 持久化
// ============================================

/** 加载风险记录 */
function loadRisks(): Risk[] {
  try {
    const raw = localStorage.getItem(RISKS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as Risk[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 保存风险记录 */
function saveRisks(risks: Risk[]): void {
  try {
    localStorage.setItem(RISKS_KEY, JSON.stringify(risks));
  } catch {
    // 静默降级
  }
}

/** 加载任务列表 */
function loadTasks(): TaskLite[] {
  try {
    const raw = localStorage.getItem(TASKS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as TaskLite[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

// ============================================
// 风险自动识别
// ============================================

/**
 * 基于任务列表自动识别风险
 * - 进度风险：已延期未完成的任务
 * - 资源风险：同一成员承担 ≥3 个进行中任务
 * - 技术风险：阻塞状态的任务
 * - 外部风险：暂无自动识别条件，需手动添加
 */
function autoIdentifyRisks(tasks: TaskLite[]): Risk[] {
  const now = Date.now();
  const risks: Risk[] = [];
  const ts = new Date().toISOString();

  // 1. 进度风险：延期任务
  tasks.forEach((t) => {
    if (t.status === "done") return;
    const due = new Date(t.dueDate).getTime();
    if (!isNaN(due) && due < now) {
      risks.push({
        id: `auto_schedule_${t.id}`,
        title: `任务延期：${t.title}`,
        description: `任务「${t.title}」已超过截止日期 ${t.dueDate}，当前状态 ${t.status}，进度 ${t.actualProgress}%。`,
        type: "schedule",
        probability: t.priority === "urgent" || t.priority === "high" ? "high" : "medium",
        impact: t.priority === "urgent" ? "high" : "medium",
        status: "open",
        suggestion: RISK_SUGGESTIONS.schedule,
        relatedTaskId: t.id,
        source: "auto",
        createdAt: ts,
        updatedAt: ts,
      });
    }
  });

  // 2. 资源风险：同一成员承担 ≥3 个进行中任务
  const assigneeMap = new Map<string, TaskLite[]>();
  tasks.forEach((t) => {
    if (t.status === "done" || !t.assignee) return;
    if (!assigneeMap.has(t.assignee)) assigneeMap.set(t.assignee, []);
    assigneeMap.get(t.assignee)!.push(t);
  });
  assigneeMap.forEach((taskList, assignee) => {
    if (taskList.length >= 3) {
      risks.push({
        id: `auto_resource_${assignee}`,
        title: `成员过载：${assignee}`,
        description: `成员「${assignee}」当前承担 ${taskList.length} 个未完成任务，存在资源过载风险。`,
        type: "resource",
        probability: "high",
        impact: taskList.length >= 5 ? "high" : "medium",
        status: "open",
        suggestion: RISK_SUGGESTIONS.resource,
        source: "auto",
        createdAt: ts,
        updatedAt: ts,
      });
    }
  });

  // 3. 技术风险：阻塞任务
  tasks.forEach((t) => {
    if (t.status === "blocked") {
      risks.push({
        id: `auto_technical_${t.id}`,
        title: `任务阻塞：${t.title}`,
        description: `任务「${t.title}」处于阻塞状态，需排查技术障碍。负责人：${t.assignee || "未分配"}。`,
        type: "technical",
        probability: "high",
        impact: t.priority === "urgent" ? "high" : "medium",
        status: "open",
        suggestion: RISK_SUGGESTIONS.technical,
        relatedTaskId: t.id,
        source: "auto",
        createdAt: ts,
        updatedAt: ts,
      });
    }
  });

  return risks;
}

// ============================================
// 工具函数
// ============================================

/** 风险评分（概率 × 影响，用于矩阵定位） */
function riskScore(probability: RiskLevel, impact: RiskLevel): number {
  const pMap = { low: 1, medium: 2, high: 3 } as const;
  const iMap = { low: 1, medium: 2, high: 3 } as const;
  return pMap[probability] * iMap[impact];
}

/** 根据评分返回严重度颜色 */
function severityColor(probability: RiskLevel, impact: RiskLevel): string {
  const score = riskScore(probability, impact);
  if (score >= 6) return "#EF4444"; // 高危
  if (score >= 3) return "#F59E0B"; // 中危
  return "#10B981"; // 低危
}

/** 格式化日期 */
function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

// ============================================
// 主组件
// ============================================

export default function RiskManager() {
  const [risks, setRisks] = useState<Risk[]>([]);
  const [tasks, setTasks] = useState<TaskLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [typeFilter, setTypeFilter] = useState<RiskType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<RiskStatus | "all">("all");

  // ============================================
  // 加载数据：合并持久化风险 + 自动识别风险
  // ============================================
  const loadData = useCallback(() => {
    setLoading(true);
    const loadedTasks = loadTasks();
    const loadedRisks = loadRisks();
    const auto = autoIdentifyRisks(loadedTasks);

    // 合并：手动风险全部保留 + 自动风险（若手动已有同 id 则不重复）
    const manualRisks = loadedRisks.filter((r) => r.source === "manual");
    const existingAutoIds = new Set(loadedRisks.map((r) => r.id));
    const newAutoRisks = auto.filter((a) => !existingAutoIds.has(a.id));
    // 已持久化的自动风险（用户可能已调整过），也保留
    const persistedAuto = loadedRisks.filter((r) => r.source === "auto");
    const merged = [...manualRisks, ...persistedAuto, ...newAutoRisks];

    setTasks(loadedTasks);
    setRisks(merged);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // 风险 CRUD
  // ============================================
  const persistRisks = useCallback((list: Risk[]) => {
    setRisks(list);
    saveRisks(list);
  }, []);

  const handleSaveRisk = useCallback(
    (data: Omit<Risk, "id" | "createdAt" | "updatedAt" | "source">) => {
      const now = new Date().toISOString();
      if (editingRisk) {
        const updated = risks.map((r) =>
          r.id === editingRisk.id
            ? { ...r, ...data, updatedAt: now }
            : r
        );
        persistRisks(updated);
      } else {
        const newRisk: Risk = {
          ...data,
          id: `risk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          source: "manual",
          createdAt: now,
          updatedAt: now,
        };
        persistRisks([newRisk, ...risks]);
      }
      setShowEditModal(false);
      setEditingRisk(null);
    },
    [editingRisk, risks, persistRisks]
  );

  const handleDeleteRisk = useCallback(
    (riskId: string) => {
      const updated = risks.filter((r) => r.id !== riskId);
      persistRisks(updated);
    },
    [risks, persistRisks]
  );

  /** 调整风险状态 */
  const handleStatusChange = useCallback(
    (riskId: string, status: RiskStatus) => {
      const updated = risks.map((r) =>
        r.id === riskId
          ? { ...r, status, updatedAt: new Date().toISOString() }
          : r
      );
      persistRisks(updated);
    },
    [risks, persistRisks]
  );

  /** 调整概率 / 影响 */
  const handleLevelChange = useCallback(
    (riskId: string, field: "probability" | "impact", level: RiskLevel) => {
      const updated = risks.map((r) =>
        r.id === riskId
          ? { ...r, [field]: level, updatedAt: new Date().toISOString() }
          : r
      );
      persistRisks(updated);
    },
    [risks, persistRisks]
  );

  // ============================================
  // 筛选与统计
  // ============================================
  const filteredRisks = useMemo(() => {
    return risks.filter((r) => {
      if (typeFilter !== "all" && r.type !== typeFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      return true;
    });
  }, [risks, typeFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: risks.length,
      open: risks.filter((r) => r.status === "open").length,
      handling: risks.filter((r) => r.status === "handling").length,
      closed: risks.filter((r) => r.status === "closed").length,
      high: risks.filter(
        (r) => riskScore(r.probability, r.impact) >= 6
      ).length,
    };
  }, [risks]);

  /** 风险矩阵：3x3 网格，按概率 × 影响统计数量 */
  const riskMatrix = useMemo(() => {
    // 行：影响（高/中/低，从上到下），列：概率（低/中/高，从左到右）
    const matrix: Record<RiskLevel, Record<RiskLevel, Risk[]>> = {
      high: { low: [], medium: [], high: [] },
      medium: { low: [], medium: [], high: [] },
      low: { low: [], medium: [], high: [] },
    };
    risks.forEach((r) => {
      matrix[r.impact][r.probability].push(r);
    });
    return matrix;
  }, [risks]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-orange-500/15 border border-orange-500/30">
            <ShieldAlert size={13} className="text-orange-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              风险管理
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Risk Manager · 共 {stats.total} · 开放 {stats.open} · 处理中{" "}
              {stats.handling} · 已闭环 {stats.closed} · 高危 {stats.high}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 新增风险 */}
          <button
            onClick={() => {
              setEditingRisk(null);
              setShowEditModal(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-500/15 border border-orange-500/30 hover:bg-orange-500/25 transition-colors"
          >
            <Plus size={11} className="text-orange-400" />
            <span className="text-[9px] font-mono text-orange-300 uppercase tracking-wider">
              新增风险
            </span>
          </button>

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* 主体：左侧风险矩阵 + 右侧风险列表 */}
      <div className="grid grid-cols-12 gap-3 p-4">
        {/* 左侧：风险矩阵 */}
        <div className="col-span-4 space-y-2">
          <div className="flex items-center gap-1.5">
            <Sparkles size={10} className="text-orange-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              风险矩阵（概率 × 影响）
            </span>
          </div>
          <RiskMatrix matrix={riskMatrix} />
          {/* 图例 */}
          <div className="flex items-center gap-2 text-[8px] font-mono text-gray-500">
            <span className="flex items-center gap-0.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: "#10B981" }} />
              低危
            </span>
            <span className="flex items-center gap-0.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: "#F59E0B" }} />
              中危
            </span>
            <span className="flex items-center gap-0.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: "#EF4444" }} />
              高危
            </span>
          </div>
        </div>

        {/* 右侧：风险列表 */}
        <div className="col-span-8 space-y-2">
          {/* 筛选栏 */}
          <div className="flex items-center gap-2">
            <select
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as RiskType | "all")
              }
              className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.04] text-[9px] font-mono text-gray-400 outline-none cursor-pointer"
            >
              <option value="all">全部类型</option>
              {(Object.keys(RISK_TYPE_META) as RiskType[]).map((t) => (
                <option key={t} value={t}>
                  {RISK_TYPE_META[t].label}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as RiskStatus | "all")
              }
              className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.04] text-[9px] font-mono text-gray-400 outline-none cursor-pointer"
            >
              <option value="all">全部状态</option>
              {(Object.keys(STATUS_META) as RiskStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_META[s].label}
                </option>
              ))}
            </select>
            <span className="ml-auto text-[9px] font-mono text-gray-600">
              {filteredRisks.length}/{risks.length}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 size={20} className="animate-spin text-orange-400" />
              <span className="ml-2 text-[11px] font-mono text-gray-500">
                加载风险数据...
              </span>
            </div>
          ) : filteredRisks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <ShieldAlert size={28} className="text-gray-700 mb-2" />
              <p className="text-[11px] font-mono text-gray-500">
                暂无风险记录，系统会自动识别延期/阻塞/过载风险
              </p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
              {filteredRisks.map((r) => (
                <RiskRow
                  key={r.id}
                  risk={r}
                  onEdit={() => {
                    setEditingRisk(r);
                    setShowEditModal(true);
                  }}
                  onDelete={() => handleDeleteRisk(r.id)}
                  onStatusChange={(s) => handleStatusChange(r.id, s)}
                  onLevelChange={(f, l) => handleLevelChange(r.id, f, l)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 风险编辑弹窗 */}
      <AnimatePresence>
        {showEditModal && (
          <RiskEditModal
            risk={editingRisk}
            onSubmit={handleSaveRisk}
            onClose={() => {
              setShowEditModal(false);
              setEditingRisk(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 风险矩阵 3x3
// ============================================
function RiskMatrix({
  matrix,
}: {
  matrix: Record<RiskLevel, Record<RiskLevel, Risk[]>>;
}) {
  const impactOrder: RiskLevel[] = ["high", "medium", "low"];
  const probOrder: RiskLevel[] = ["low", "medium", "high"];

  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-2">
      {/* 列头：概率 */}
      <div className="grid grid-cols-4 gap-1 mb-1">
        <div className="text-[8px] font-mono text-gray-600 text-center">
          影响\概率
        </div>
        {probOrder.map((p) => (
          <div
            key={p}
            className="text-[8px] font-mono text-center"
            style={{ color: LEVEL_META[p].color }}
          >
            {LEVEL_META[p].label}
          </div>
        ))}
      </div>
      {/* 矩阵行 */}
      {impactOrder.map((impact) => (
        <div key={impact} className="grid grid-cols-4 gap-1 mb-1">
          <div
            className="text-[8px] font-mono text-center flex items-center justify-center"
            style={{ color: LEVEL_META[impact].color }}
          >
            {LEVEL_META[impact].label}
          </div>
          {probOrder.map((prob) => {
            const cellRisks = matrix[impact][prob];
            const color = severityColor(prob, impact);
            const count = cellRisks.length;
            return (
              <div
                key={prob}
                className="h-10 rounded border flex flex-col items-center justify-center transition-colors hover:bg-white/[0.04] cursor-default"
                style={{
                  background: count > 0 ? `${color}20` : "transparent",
                  borderColor: count > 0 ? `${color}40` : "#ffffff10",
                }}
                title={`${count} 个风险`}
              >
                <span
                  className="text-[14px] font-mono font-semibold"
                  style={{ color: count > 0 ? color : "#4B5563" }}
                >
                  {count}
                </span>
                {count > 0 && (
                  <span className="text-[7px] font-mono text-gray-500">
                    {cellRisks.filter((r) => r.status !== "closed").length} 待处理
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ============================================
// 风险行
// ============================================
function RiskRow({
  risk,
  onEdit,
  onDelete,
  onStatusChange,
  onLevelChange,
}: {
  risk: Risk;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: RiskStatus) => void;
  onLevelChange: (field: "probability" | "impact", level: RiskLevel) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const typeMeta = RISK_TYPE_META[risk.type];
  const statusMeta = STATUS_META[risk.status];
  const TIcon = typeMeta.icon;
  const SIcon = statusMeta.icon;
  const sevColor = severityColor(risk.probability, risk.impact);

  return (
    <div
      className="rounded-md border bg-white/[0.02] overflow-hidden"
      style={{ borderColor: `${sevColor}30` }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          {/* 类型图标 */}
          <div
            className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
            style={{
              background: `${typeMeta.color}15`,
              border: `1px solid ${typeMeta.color}30`,
            }}
          >
            <TIcon size={11} style={{ color: typeMeta.color }} />
          </div>

          {/* 标题 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-200 font-medium truncate">
                {risk.title}
              </span>
              {risk.source === "auto" && (
                <span className="px-1 rounded text-[7px] font-mono uppercase bg-cyan-500/15 text-cyan-400">
                  自动
                </span>
              )}
            </div>
            <div className="text-[9px] font-mono text-gray-600 truncate">
              {typeMeta.label}
            </div>
          </div>

          {/* 概率/影响标签 */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span
              className="px-1 rounded text-[8px] font-mono"
              style={{
                background: `${LEVEL_META[risk.probability].color}20`,
                color: LEVEL_META[risk.probability].color,
              }}
            >
              P:{LEVEL_META[risk.probability].label}
            </span>
            <span
              className="px-1 rounded text-[8px] font-mono"
              style={{
                background: `${LEVEL_META[risk.impact].color}20`,
                color: LEVEL_META[risk.impact].color,
              }}
            >
              I:{LEVEL_META[risk.impact].label}
            </span>
          </div>

          {/* 状态 */}
          <span
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase flex-shrink-0"
            style={{
              background: `${statusMeta.color}20`,
              color: statusMeta.color,
            }}
          >
            <SIcon size={8} />
            {statusMeta.label}
          </span>
        </div>
      </button>

      {/* 展开详情 */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/[0.03]"
          >
            <div className="p-2.5 space-y-2">
              {/* 描述 */}
              <p className="text-[10px] text-gray-400 leading-snug">
                {risk.description}
              </p>

              {/* 应对建议 */}
              <div className="flex items-start gap-1.5 px-2 py-1.5 rounded bg-purple-500/[0.05] border border-purple-500/20">
                <Lightbulb size={10} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-[8px] font-mono text-purple-400 uppercase tracking-wider mb-0.5">
                    应对建议
                  </div>
                  <p className="text-[10px] text-gray-300 leading-snug whitespace-pre-wrap">
                    {risk.suggestion}
                  </p>
                </div>
              </div>

              {/* 评估调整 */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[8px] font-mono text-gray-600 uppercase tracking-wider mb-0.5">
                    概率
                  </label>
                  <select
                    value={risk.probability}
                    onChange={(e) =>
                      onLevelChange("probability", e.target.value as RiskLevel)
                    }
                    className="w-full px-1.5 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] font-mono text-gray-300 outline-none cursor-pointer"
                  >
                    <option value="low">低</option>
                    <option value="medium">中</option>
                    <option value="high">高</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[8px] font-mono text-gray-600 uppercase tracking-wider mb-0.5">
                    影响
                  </label>
                  <select
                    value={risk.impact}
                    onChange={(e) =>
                      onLevelChange("impact", e.target.value as RiskLevel)
                    }
                    className="w-full px-1.5 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] font-mono text-gray-300 outline-none cursor-pointer"
                  >
                    <option value="low">低</option>
                    <option value="medium">中</option>
                    <option value="high">高</option>
                  </select>
                </div>
              </div>

              {/* 状态调整 + 操作 */}
              <div className="flex items-center gap-2 pt-1 border-t border-white/[0.03]">
                <span className="text-[8px] font-mono text-gray-600 uppercase tracking-wider">
                  状态
                </span>
                {(["open", "handling", "closed"] as RiskStatus[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => onStatusChange(s)}
                    className={`px-1.5 py-0.5 rounded text-[8px] font-mono uppercase transition-colors ${
                      risk.status === s
                        ? "bg-white/[0.08] text-white"
                        : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
                    }`}
                  >
                    {STATUS_META[s].label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-0.5">
                  <button
                    onClick={onEdit}
                    className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
                    title="编辑"
                  >
                    <Pencil size={9} />
                  </button>
                  <button
                    onClick={onDelete}
                    className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors"
                    title="删除"
                  >
                    <Trash2 size={9} />
                  </button>
                </div>
              </div>

              {/* 时间信息 */}
              <div className="flex items-center justify-between text-[8px] font-mono text-gray-700">
                <span>创建：{formatDate(risk.createdAt)}</span>
                <span>更新：{formatDate(risk.updatedAt)}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 风险编辑弹窗
// ============================================
function RiskEditModal({
  risk,
  onSubmit,
  onClose,
}: {
  risk: Risk | null;
  onSubmit: (data: Omit<Risk, "id" | "createdAt" | "updatedAt" | "source">) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    title: risk?.title || "",
    description: risk?.description || "",
    type: risk?.type || ("technical" as RiskType),
    probability: risk?.probability || ("medium" as RiskLevel),
    impact: risk?.impact || ("medium" as RiskLevel),
    status: risk?.status || ("open" as RiskStatus),
    suggestion: risk?.suggestion || "",
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!form.title.trim()) {
      setError("请输入风险标题");
      return;
    }
    if (!form.description.trim()) {
      setError("请输入风险描述");
      return;
    }
    // 若未填写建议，按类型自动生成
    const suggestion = form.suggestion.trim() || RISK_SUGGESTIONS[form.type];
    onSubmit({ ...form, suggestion });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-xl border border-orange-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
              {risk ? (
                <Pencil size={13} className="text-orange-400" />
              ) : (
                <Plus size={13} className="text-orange-400" />
              )}
            </div>
            <h3 className="text-[13px] font-semibold text-white">
              {risk ? "编辑风险" : "新增风险"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          <FormField label="风险标题">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="简明描述风险"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-orange-500/30"
            />
          </FormField>

          <FormField label="风险描述">
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="详细描述风险成因和影响"
              rows={3}
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-orange-500/30 resize-none"
            />
          </FormField>

          <FormField label="风险类型">
            <select
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as RiskType })
              }
              className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
            >
              {(Object.keys(RISK_TYPE_META) as RiskType[]).map((t) => (
                <option key={t} value={t}>
                  {RISK_TYPE_META[t].label}
                </option>
              ))}
            </select>
          </FormField>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="概率">
              <select
                value={form.probability}
                onChange={(e) =>
                  setForm({ ...form, probability: e.target.value as RiskLevel })
                }
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
              >
                <option value="low">低</option>
                <option value="medium">中</option>
                <option value="high">高</option>
              </select>
            </FormField>
            <FormField label="影响">
              <select
                value={form.impact}
                onChange={(e) =>
                  setForm({ ...form, impact: e.target.value as RiskLevel })
                }
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
              >
                <option value="low">低</option>
                <option value="medium">中</option>
                <option value="high">高</option>
              </select>
            </FormField>
            <FormField label="状态">
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as RiskStatus })
                }
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
              >
                <option value="open">开放</option>
                <option value="handling">处理中</option>
                <option value="closed">已闭环</option>
              </select>
            </FormField>
          </div>

          <FormField label="应对建议（留空将按类型自动生成）">
            <textarea
              value={form.suggestion}
              onChange={(e) =>
                setForm({ ...form, suggestion: e.target.value })
              }
              placeholder={RISK_SUGGESTIONS[form.type]}
              rows={3}
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-orange-500/30 resize-none"
            />
          </FormField>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-orange-500/20 border border-orange-500/30 text-[10px] font-mono text-orange-300 hover:bg-orange-500/30 transition-colors"
          >
            <AlertTriangle size={10} />
            {risk ? "保存" : "创建"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 表单字段包装
// ============================================
function FormField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
